import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api, { brl } from "../lib/api";
import {
  Search, ShieldCheck, AlertTriangle, Loader2, User, Phone,
  Smartphone, TrendingUp, Wrench, Calendar,
} from "lucide-react";

export default function ImeiLookup() {
  const [params, setParams] = useSearchParams();
  const initial = params.get("imei") || "";
  const [imei, setImei] = useState(initial);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const search = useCallback(
    (val) => {
      const digits = String(val || "").replace(/\D/g, "");
      if (!digits) return;
      if (digits.length < 6) {
        setErr("Digite pelo menos 6 dígitos do IMEI");
        setData(null);
        return;
      }
      setLoading(true);
      setErr(null);
      api
        .get(`/imei/${digits}/history`)
        .then((r) => {
          setData(r.data);
          setParams({ imei: digits });
        })
        .catch((e) => {
          setErr(e?.response?.data?.detail || "Erro ao buscar");
          setData(null);
        })
        .finally(() => setLoading(false));
    },
    [setParams]
  );

  useEffect(() => {
    if (initial) search(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = (e) => {
    e.preventDefault();
    search(imei);
  };

  const w = data?.warranty;

  return (
    <div className="space-y-6" data-testid="imei-lookup-page">
      <header>
        <div className="text-xs uppercase tracking-[0.25em] text-[#2563EB] font-semibold mb-1">
          Consulta por Aparelho
        </div>
        <h1 className="font-display text-4xl font-bold">Histórico por IMEI</h1>
        <p className="text-sm text-slate-500 mt-1">
          Digite o IMEI do aparelho para ver toda a passagem dele pela loja, valor total gasto e status de garantia.
        </p>
      </header>

      {/* Search */}
      <form onSubmit={submit} className="bg-white border border-slate-200 rounded-sm p-4">
        <div className="flex gap-2 items-end">
          <div className="flex-1">
            <label className="block text-xs uppercase tracking-widest text-slate-600 mb-1">
              IMEI do aparelho
            </label>
            <input
              autoFocus
              value={imei}
              onChange={(e) => setImei(e.target.value)}
              placeholder="Ex: 356789012345678"
              className="w-full px-3 py-3 border border-slate-300 rounded-sm text-lg font-mono"
              data-testid="imei-lookup-input"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="imei-lookup-search"
            className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white px-6 py-3 rounded-sm font-semibold flex items-center gap-2 disabled:opacity-50"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
            Consultar
          </button>
        </div>
      </form>

      {err && (
        <div className="bg-red-50 border border-red-200 text-red-800 p-3 rounded-sm text-sm" data-testid="imei-lookup-error">
          {err}
        </div>
      )}

      {data && (
        <>
          {/* Header do resultado */}
          <div className="bg-white border border-slate-200 rounded-sm p-5" data-testid="imei-lookup-result">
            <div className="flex flex-wrap justify-between items-start gap-4">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1">Aparelho</div>
                <div className="font-display text-2xl font-bold text-slate-900 flex items-center gap-2">
                  <Smartphone size={22} className="text-[#2563EB]" />
                  {data.last_device?.brand || "—"} {data.last_device?.device || ""}
                </div>
                <div className="text-xs font-mono text-slate-500 mt-1">IMEI: {data.imei}</div>
              </div>
              <div className="text-right">
                <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1">Total gasto</div>
                <div className="font-display text-2xl font-bold text-[#2563EB]">{brl(data.total_spent)}</div>
                <div className="text-xs text-slate-500">{data.total_visits} passagem(ns) pela loja</div>
              </div>
            </div>

            {data.last_device?.customer_name && (
              <div className="mt-4 pt-4 border-t border-slate-100 grid sm:grid-cols-2 gap-2 text-sm">
                <div className="flex items-center gap-2">
                  <User size={13} className="text-slate-400" />
                  <span className="text-slate-500">Último cliente:</span>
                  <b>{data.last_device.customer_name}</b>
                </div>
                {data.last_device.customer_phone && (
                  <div className="flex items-center gap-2">
                    <Phone size={13} className="text-slate-400" />
                    <span className="text-slate-500">Tel:</span>
                    <b>{data.last_device.customer_phone}</b>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Garantia */}
          {w?.active ? (
            <div
              className="bg-gradient-to-br from-emerald-50 to-white border-l-4 border-emerald-500 rounded-sm p-5"
              data-testid="imei-warranty-card"
            >
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-sm bg-emerald-500 text-white flex items-center justify-center flex-shrink-0">
                  <ShieldCheck size={22} />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-emerald-700 font-bold mb-1">
                    🛡️ Garantia ATIVA
                  </div>
                  <div className="font-display text-xl font-bold text-slate-900">
                    {w.days_remaining} dia(s) restantes
                  </div>
                  <div className="text-sm text-slate-700 mt-1">
                    Válida até <b>{(w.expires_at || "").slice(0, 10).split("-").reverse().join("/")}</b>{" "}
                    · OS <span className="font-mono">#{w.from_os_short}</span>
                  </div>
                  <div className="text-sm text-slate-600 mt-2">
                    <b>Serviço garantido:</b> {w.service}
                  </div>
                  <div className="text-xs text-emerald-700 mt-2">
                    ✓ Se o problema atual for do mesmo serviço, o reparo é gratuito.
                  </div>
                </div>
              </div>
            </div>
          ) : data.history.some((h) => h.status === "entregue") ? (
            <div
              className="bg-slate-50 border border-slate-200 rounded-sm p-4 flex items-center gap-3"
              data-testid="imei-warranty-expired"
            >
              <ShieldCheck size={18} className="text-slate-400" />
              <div className="text-sm text-slate-600">
                Garantia dos últimos serviços já expirou (superior a 3 meses).
              </div>
            </div>
          ) : null}

          {/* Problemas recorrentes */}
          {data.recurring_problems?.length > 0 && (
            <div
              className="bg-amber-50 border border-amber-300 rounded-sm p-4"
              data-testid="imei-recurring-card"
            >
              <div className="flex items-start gap-2">
                <AlertTriangle size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-amber-800 font-bold mb-1">
                    ⚠️ Problemas recorrentes detectados
                  </div>
                  <ul className="text-sm text-amber-900 space-y-0.5">
                    {data.recurring_problems.map((p) => (
                      <li key={p.problem}>
                        • <b>{p.problem}</b> — apareceu <b>{p.count}x</b>
                      </li>
                    ))}
                  </ul>
                  <div className="text-xs text-amber-700 mt-2">
                    Possível defeito estrutural do aparelho — considere avisar o cliente.
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Histórico */}
          <div className="bg-white border border-slate-200 rounded-sm">
            <div className="p-4 border-b border-slate-200">
              <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Linha do tempo</div>
              <h2 className="font-display text-lg font-bold">
                Todas as passagens ({data.total_visits})
              </h2>
            </div>
            {data.history.length === 0 ? (
              <div className="p-8 text-center text-slate-500 text-sm">
                Nenhum atendimento encontrado para este IMEI.
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {data.history.map((h) => {
                  const d = (h.created_at || "").slice(0, 10);
                  const [y, m, dd] = d.split("-");
                  const display = y ? `${dd}/${m}/${y}` : "—";
                  const statusColor =
                    h.status === "entregue"
                      ? "bg-blue-100 text-blue-700"
                      : h.status === "pronto"
                      ? "bg-emerald-100 text-emerald-700"
                      : h.status === "cancelado"
                      ? "bg-red-100 text-red-700"
                      : "bg-slate-100 text-slate-700";
                  return (
                    <div key={h.id} className="p-4 flex flex-wrap items-start gap-4" data-testid={`imei-history-row-${h.id}`}>
                      <div className="flex items-center gap-2 w-full sm:w-32 flex-shrink-0">
                        <Calendar size={13} className="text-slate-400" />
                        <span className="text-sm font-mono text-slate-600">{display}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-mono text-slate-500 mb-0.5">#{h.short_id}</div>
                        <div className="font-semibold text-slate-900 flex items-center gap-1">
                          <Wrench size={13} className="text-slate-400 flex-shrink-0" />
                          {h.problem}
                        </div>
                        {h.diagnosis && (
                          <div className="text-sm text-slate-600 mt-0.5">
                            <b>Serviço:</b> {h.diagnosis}
                          </div>
                        )}
                        <div className="text-xs text-slate-500 mt-0.5">Cliente: {h.customer_name || "—"}</div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="font-bold text-slate-900">{brl(h.price)}</div>
                        <span
                          className={`text-[9px] uppercase font-bold px-2 py-0.5 rounded-sm mt-1 inline-block ${statusColor}`}
                        >
                          {h.status_label}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
